import { LocalDate } from "@js-joda/core";
import { AxiosError } from "axios";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { Text as TextV2 } from "components/designV2/text/text";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  HeaderRightAligned,
  IndividualSalaryFilters,
  Logo,
  LogoContainer,
  PeriodNavigatorContainer,
  SalaryNoticeContainer,
  SalaryPageBottomMargin,
  TooltipContainer,
} from "./salary.styled";
import {
  EmptyStateWrapper,
  RowActions,
  SalaryNoticeRowCard,
} from "./individualSalary.styled";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { Modal } from "components/designV2/modal/modal";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { SalaryNotice } from "model/Salary";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import { formatDuration } from "utils/utils";
import { Api } from "services/api/api.service";
import { SummaryIndividualSalary } from "./summaryIndividualSalary.component";
import { PaymentMethod } from "model/Gig";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { FileViewer } from "../fileViewer/fileViewer.component";
import { IconButton } from "web-apps/company/components/timeReportsV2/components/views/person/components/row/personSummaryRow.styled";
import { CompanyWorker } from "model/Company";

export const formatTimePeriod = (timePeriod: string): string => {
  const year = timePeriod.slice(0, 4);
  const month = timePeriod.slice(4, 6);

  return `${year}-${month}`;
};

export interface ModalState {
  open: boolean;
  workerId?: number;
  pdfId?: number;
  period?: string;
  workerName?: string;
}

export const IndividualSalary = () => {
  const { t } = useTranslation();
  const [{ company }] = useCompanyStore();
  const [salaryNotices, setSalaryNotices] = useState<SalaryNotice[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<CompanyWorker>();
  const [currentDate, setCurrentDate] = useState(LocalDate.now());
  const [workers, setWorkers] = useState<CompanyWorker[]>([]);
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    workerId: undefined,
    pdfId: undefined,
    period: undefined,
    workerName: undefined,
  });

  useEffect(() => {
    if (!company?.id) return;
    Api()
      .company.worker.getCompanyWorkers(company.id, false)
      .then((res) => setWorkers(res.data))
      .catch((e: AxiosError) => console.error("Error fetching workers", e));
  }, [company?.id]);

  const workerOptions = useMemo<SearchableDropdownOption[]>(
    () =>
      workers.map((w) => ({
        value: w.id.toString(),
        label: `${w.firstName} ${w.lastName}`,
      })),
    [workers],
  );

  const getSalaries = useCallback(() => {
    if (!company?.id || !selectedWorker) {
      setSalaryNotices([]);
      return;
    }

    Api()
      .company.salary.getWorkerSalaries(
        company.id,
        selectedWorker.id,
        currentDate.year(),
      )
      .then((res) => {
        setSalaryNotices(res.data.salaryNotices);
      })
      .catch((e: AxiosError) => {
        console.error("Error when getting salaries", e);
      });
  }, [company?.id, selectedWorker, currentDate]);

  const openSalaryPdf = useCallback((salary: SalaryNotice) => {
    setModalState({
      open: true,
      workerId: salary.workerId,
      pdfId: salary.id,
      period: salary.period,
      workerName: salary.workerName,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      open: false,
      workerId: undefined,
      pdfId: undefined,
      period: undefined,
      workerName: undefined,
    });
  }, []);

  useEffect(() => {
    getSalaries();
  }, [getSalaries]);

  const renderSalaryNotices = useCallback(() => {
    if (!selectedWorker) {
      return (
        <EmptyStateWrapper>
          <TextV2 typography="b2" color={ColorV2.Blue1} align="center">
            {t("Salary.SearchToViewPayslips")}
          </TextV2>
        </EmptyStateWrapper>
      );
    }

    if (salaryNotices.length === 0) {
      return (
        <EmptyStateWrapper>
          <TextV2 typography="b2" color={ColorV2.Blue1} align="center">
            {t("Salary.NoSalaryDataForPerson", {
              name: `${selectedWorker.firstName} ${selectedWorker.lastName}`,
              year: currentDate.year(),
            })}
          </TextV2>
        </EmptyStateWrapper>
      );
    }

    return (
      <>
        <SalaryNoticeContainer>
          {salaryNotices.map((salary, index) => {
            const currency = salary.currency === "SEK" ? " kr" : "";
            const isLastItem = index === salaryNotices.length - 1;

            return (
              <SalaryNoticeRowCard
                $isLastItem={isLastItem}
                key={`salary-${salary.id}-${index}`}
              >
                <LabeledValue
                  value={formatTimePeriod(salary.period)}
                  label={t("Salary.Period")}
                  variant="small"
                />
                <LabeledValue
                  value={formatDuration(salary.workedHours.toString())}
                  label={t("Salary.TotalWorkTime")}
                  variant="small"
                />
                <LabeledValue
                  value={`${salary.grossSalary}${currency}`}
                  label={t("Salary.GrossSalary")}
                  variant="small"
                />
                <LabeledValue
                  value={`${salary.netSalary}${currency}`}
                  label={t("Salary.NetSalary")}
                  variant="small"
                />
                <LabeledValue
                  value={`${salary.preliminaryTax}${currency}`}
                  label={t("Salary.Tax")}
                  variant="small"
                />
                <LabeledValue
                  value={`${salary.payrollTax}${currency}`}
                  label={t("Salary.PayrollTax")}
                  variant="small"
                />
                <LabeledValue
                  value={`${salary.deductionAmount}${currency}`}
                  label={t("Salary.DeductionAmount")}
                  variant="small"
                />
                <RowActions>
                  <TooltipHover text={t("Salary.ViewSalaryNoticeTooltip")}>
                    <IconButton onClick={() => openSalaryPdf(salary)}>
                      <CustomIcon
                        name="v2/document"
                        size="20px"
                        color={ColorV2.Blue1}
                      />
                    </IconButton>
                  </TooltipHover>
                  {salary.payrollType === PaymentMethod.SIMFLEXITY_PAY && (
                    <LogoContainer>
                      <Logo src={SimflexityPayLogo} alt="SimflexityPay logo" />
                    </LogoContainer>
                  )}
                </RowActions>
              </SalaryNoticeRowCard>
            );
          })}
        </SalaryNoticeContainer>
        {salaryNotices.length > 0 && (
          <SummaryIndividualSalary
            salaryNotices={salaryNotices}
            selectedYear={currentDate.year()}
          />
        )}
      </>
    );
  }, [salaryNotices, selectedWorker, currentDate, t, openSalaryPdf]);

  const renderPdfModal = useCallback(() => {
    const { open, workerId, pdfId, period, workerName } = modalState;

    if (!open || !company || !workerId || !pdfId || !period || !workerName) {
      return null;
    }

    return (
      <Modal
        isOpen={open}
        onClose={closeModal}
        label={t("Salary.ViewSalaryNoticeTooltip")}
        size="large"
      >
        <FileViewer
          fetchEndpoint={() =>
            Api().company.salary.generateSalaryNotice(
              company.id,
              workerId,
              pdfId,
            )
          }
          filename={`SalaryNotice_${period}_${workerName}.pdf`}
          type="application/pdf"
        />
      </Modal>
    );
  }, [modalState, company, closeModal, t]);

  return (
    <>
      <HeaderRightAligned>
        <TooltipContainer>
          <TooltipHover text={t("Salary.IndividualSalarySlipInfo")} arrow="up">
            <TextV2 typography="s1">
              {t(`Salary.WhatIs`, {
                name: t("Salary.IndividualSalary").toLowerCase(),
              })}
            </TextV2>
          </TooltipHover>
        </TooltipContainer>
      </HeaderRightAligned>
      <IndividualSalaryFilters>
        <SearchableDropdown
          placeholder={t("Salary.SearchForWorker")}
          options={workerOptions}
          value={selectedWorker?.id.toString()}
          leadingIconName="v2/search"
          onClear={() => setSelectedWorker(undefined)}
          onChange={(value) =>
            setSelectedWorker(workers.find((w) => w.id.toString() === value))
          }
        />
        <PeriodNavigatorContainer>
          <PeriodNavigator
            currentDate={currentDate}
            mode="year"
            onChange={setCurrentDate}
          />
        </PeriodNavigatorContainer>
        <div />
      </IndividualSalaryFilters>
      <SalaryPageBottomMargin>{renderSalaryNotices()}</SalaryPageBottomMargin>
      {renderPdfModal()}
    </>
  );
};
